
import { supabase } from '@/integrations/supabase/client';

let hasTracked = false;
let hasTrackedQrScan = false;

/**
 * Track a QR code scan from a hotel room or zone
 */
export const trackQrScan = async (
  restaurantId: string,
  menuId: string,
  options?: { roomNumber?: string; zoneId?: string; source?: string }
): Promise<void> => {
  if (!restaurantId || hasTrackedQrScan) return;
  hasTrackedQrScan = true;

  try {
    let roomId: string | null = null;
    let zoneId: string | null = options?.zoneId || null;

    // Resolve room_number to room_id
    if (options?.roomNumber) {
      const { data: room } = await supabase
        .from('hotel_rooms')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('room_number', options.roomNumber)
        .maybeSingle();
      roomId = room?.id || null;
    }

    await supabase.from('qr_scan_logs').insert({
      restaurant_id: restaurantId,
      room_id: roomId,
      zone_id: zoneId,
      menu_id: menuId,
      scan_source: options?.source || 'qr',
    });
  } catch {
    hasTrackedQrScan = false;
  }
};

/**
 * Track when a user clicks on a menu item
 * @param restaurantId - ID of the restaurant
 * @param menuItemId - ID of the menu item that was clicked
 */
export const trackMenuItemClick = async (
  restaurantId: string,
  menuItemId: string
): Promise<void> => {
  if (!restaurantId) {
    return;
  }
  
  try {
    const today = new Date().toISOString().split('T')[0];
    
    await supabase.rpc('increment_menu_view', {
      p_menu_id: menuItemId,
      p_restaurant_id: restaurantId,
      p_date: today
    });
  } catch (error) {
    // Silent error handling
  }
};

/**
 * Track when a user views a menu - with duplicate prevention
 * @param restaurantId - ID of the restaurant
 * @param menuId - ID of the menu
 */
export const trackMenuView = async (
  restaurantId: string,
  menuId: string
): Promise<void> => {
  if (!restaurantId || !menuId) {
    return;
  }
  
  // Prevent multiple tracking in the same session
  if (hasTracked) {
    return;
  }
  
  try {
    hasTracked = true;
    const today = new Date().toISOString().split('T')[0];
    
    await supabase.rpc('increment_menu_view', {
      p_menu_id: menuId,
      p_restaurant_id: restaurantId,
      p_date: today
    });
  } catch (error) {
    hasTracked = false;
  }
};

/**
 * Track when a user views during happy hour
 * @param restaurantId - ID of the restaurant
 */
export const trackHappyHourView = async (
  restaurantId: string
): Promise<void> => {
  if (!restaurantId) {
    return;
  }
  
  try {
    const today = new Date().toISOString().split('T')[0];
    
    await supabase.rpc('increment_menu_view', {
      p_menu_id: 'happy-hour',
      p_restaurant_id: restaurantId,
      p_date: today
    });
  } catch (error) {
    // Silent error handling
  }
};

/**
 * Track when a user clicks on the phone number
 * @param restaurantId - ID of the restaurant
 */
export const trackPhoneCall = async (
  restaurantId: string
): Promise<void> => {
  if (!restaurantId) {
    return;
  }
  
  try {
    const today = new Date().toISOString().split('T')[0];
    
    await supabase.rpc('increment_menu_view', {
      p_menu_id: 'phone-call',
      p_restaurant_id: restaurantId,
      p_date: today
    });
  } catch (error) {
    // Silent error handling
  }
};

/**
 * Track when a user clicks on reservation button
 * @param restaurantId - ID of the restaurant
 */
export const trackReservationClick = async (
  restaurantId: string
): Promise<void> => {
  if (!restaurantId) {
    return;
  }
  
  try {
    const today = new Date().toISOString().split('T')[0];
    
    await supabase.rpc('increment_menu_view', {
      p_menu_id: 'reservation',
      p_restaurant_id: restaurantId,
      p_date: today
    });
  } catch (error) {
    // Silent error handling
  }
};

export interface AnalyticsData {
  totalViews: number;
  totalCalls: number;
  totalReservationClicks: number;
  totalReservations: number;
  chartData: Array<{
    date: string;
    views: number;
    calls: number;
    reservationClicks: number;
    reservations: number;
  }>;
}

/**
 * Get analytics data for a restaurant
 * @param restaurantId - ID of the restaurant
 * @param startDate - Start date for analytics data
 * @param endDate - End date for analytics data
 */
export const getAnalyticsData = async (
  restaurantId: string, 
  startDate: string, 
  endDate: string
): Promise<AnalyticsData | null> => {
  if (!restaurantId) {
    return null;
  }

  try {
    // Execute ALL analytics queries in parallel for better performance
    const [viewsResult, callResult, reservationClicksResult, reservationResult] = await Promise.all([
      // Fetch menu views data - only use actual menu_views table, not clicks
      supabase
        .from('menu_views')
        .select('date, count, menu_id')
        .eq('restaurant_id', restaurantId)
        .gte('date', startDate)
        .lte('date', endDate),
      
      // Fetch phone call clicks
      supabase
        .from('menu_clicks')
        .select('date, count')
        .eq('restaurant_id', restaurantId)
        .eq('menu_id', 'phone-call')
        .gte('date', startDate)
        .lte('date', endDate),
      
      // Fetch reservation clicks
      supabase
        .from('menu_clicks')
        .select('date, count')
        .eq('restaurant_id', restaurantId)
        .eq('menu_id', 'reservation')
        .gte('date', startDate)
        .lte('date', endDate),
      
      // Fetch actual reservations - count by creation date, not reservation date
      supabase
        .from('reservations')
        .select('created_at, id')
        .eq('restaurant_id', restaurantId)
        .gte('created_at', startDate + 'T00:00:00.000Z')
        .lte('created_at', endDate + 'T23:59:59.999Z')
    ]);

    // Extract results from parallel queries
    const { data: viewsData, error: viewsError } = viewsResult;
    const { data: callData, error: callError } = callResult;
    const { data: reservationClicksData, error: reservationClicksError } = reservationClicksResult;
    const { data: reservationData, error: reservationError } = reservationResult;

    // Check for errors
    if (viewsError) throw viewsError;
    if (callError) throw callError;
    if (reservationClicksError) throw reservationClicksError;
    if (reservationError) throw reservationError;

    // Aggregate the data
    const totalViews = viewsData?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
    const totalCalls = callData?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
    const totalReservationClicks = reservationClicksData?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
    const totalReservations = reservationData?.length || 0;

    // Prepare data for chart visualization
    const dateRange = getDatesInRange(new Date(startDate), new Date(endDate));
    const chartData = dateRange.map(date => {
      const dateStr = date.toISOString().split('T')[0];
      const views = viewsData?.filter(item => item.date === dateStr).reduce((sum, item) => sum + (item.count || 0), 0) || 0;
      const calls = callData?.find(item => item.date === dateStr)?.count || 0;
      const reservationClicks = reservationClicksData?.find(item => item.date === dateStr)?.count || 0;
      
      // Count reservations by created_at date
      const reservations = reservationData?.filter(item => {
        const createdDate = new Date(item.created_at).toISOString().split('T')[0];
        return createdDate === dateStr;
      }).length || 0;
      
      return {
        date: dateStr,
        views,
        calls,
        reservationClicks,
        reservations
      };
    });

    return {
      totalViews,
      totalCalls,
      totalReservationClicks,
      totalReservations,
      chartData
    };
  } catch (error) {
    return null;
  }
};

// Helper function to get all dates in a range
const getDatesInRange = (startDate: Date, endDate: Date) => {
  const dates = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};
